import type { Asset, GpuSample, GpuState, Job, RedactedJob, RunnerInfo, StudioEvent } from "@studio/shared";
import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";

/**
 * Live state pushed by the server over SSE. The stream is personal: own jobs arrive in full, everyone
 * else's appear in the shared queue without prompts or files.
 */
interface LiveState {
  jobs: Record<string, Job>;
  gpu: GpuState | null;
  /** Everyone's unfinished jobs in queue order; own entries carry `jobId`. */
  queue: RedactedJob[];
  runners: RunnerInfo[];
  /** Newest telemetry sample per GPU. */
  gpus: GpuSample[];
  connected: boolean;
  upsertJob: (job: Job) => void;
}

export const useLive = create<LiveState>((set) => ({
  jobs: {},
  gpu: null,
  queue: [],
  runners: [],
  gpus: [],
  connected: false,
  upsertJob: (job) => set((state) => ({ jobs: { ...state.jobs, [job.id]: job } })),
}));

const FINAL = new Set(["completed", "failed", "cancelled"]);

export function isActiveJob(job: Job): boolean {
  return !FINAL.has(job.status) && job.status !== "draft";
}

export function isActiveQueued(job: RedactedJob): boolean {
  return !FINAL.has(job.status) && job.status !== "draft";
}

function mergeQueue(queue: RedactedJob[], job: RedactedJob): RedactedJob[] {
  const rest = queue.filter((q) => q.ref !== job.ref);
  if (!isActiveQueued(job)) return rest;
  return [...rest, job].sort((a, b) => (a.queuePosition ?? 99) - (b.queuePosition ?? 99));
}

/** Opens the event stream once for the app; reconnects on its own (EventSource does). */
export function connectEvents(queryClient: QueryClient): () => void {
  let source: EventSource | null = null;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const open = () => {
    source = new EventSource("/api/events");
    source.onopen = () => useLive.setState({ connected: true });
    source.onerror = () => {
      useLive.setState({ connected: false });
      if (source?.readyState === EventSource.CLOSED) {
        retry = setTimeout(open, 3000);
      }
    };
    source.onmessage = (message) => {
      let event: StudioEvent;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      switch (event.type) {
        case "hello": {
          const jobs: Record<string, Job> = {};
          for (const job of event.jobs) jobs[job.id] = job;
          useLive.setState({ jobs, gpu: event.gpu, queue: event.queue, runners: event.runners, connected: true });
          break;
        }
        case "job": {
          const previous = useLive.getState().jobs[event.job.id];
          useLive.getState().upsertJob(event.job);
          queryClient.setQueryData(["job", event.job.id], event.job);
          if (event.job.status === "completed" && previous?.status !== "completed") {
            queryClient.invalidateQueries({ queryKey: ["assets"] });
          }
          break;
        }
        case "queue":
          useLive.setState((state) => ({ queue: mergeQueue(state.queue, event.job) }));
          break;
        case "gpu":
          useLive.setState({ gpu: event.gpu });
          break;
        case "asset":
          queryClient.setQueryData<Asset>(["asset", event.asset.id], event.asset);
          queryClient.invalidateQueries({ queryKey: ["assets"] });
          break;
        case "runners":
          useLive.setState({ runners: event.runners });
          break;
        case "telemetry":
          useLive.setState({ gpus: event.gpus });
          break;
      }
    };
  };

  open();
  return () => {
    clearTimeout(retry);
    source?.close();
  };
}
